import { Box, Grow, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState } from 'react';
// import Assets from '../../../../assets/assets';
import { OrderId } from '../../../../types/order';
import KoluluBoxInfoBox from '../KolulaBoxInfoBox';
import CP82BoxDoor from './CP82BoxDoor';

const Root = styled(Box)(({ theme }) => ({
    backgroundColor: 'white',
    width: '240px',
    height: '302px',
    margin: '0 auto',
    padding: '0px',
    boxSizing: 'border-box',
    position: 'relative',
    borderRadius: '8px',
    border: '1px solid black',
    overflow: 'hidden',
    transform: 'scale(135%) translateY(45px)',
}));

const Rectangle = styled(Box)(({ theme }) => ({
    width: '243px',
    height: '30px',
    position: 'absolute',
    backgroundColor: theme.palette.background.paper,
}));

const Info = styled(IconButton)(({ theme }) => ({
    color: theme.palette.background.default,
    width: '30px',
    height: '30px',
    position: 'absolute',
    right: 5,
    top: 0,
}));

const Title = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: '13px',
    fontWeight: 'bold',
    marginLeft: '10px',
    // Reserve room for the absolutely-positioned info icon and let long
    // station names wrap instead of sliding under it (#1035 item 2).
    paddingRight: '40px',
    lineHeight: 1.15,
    overflowWrap: 'anywhere',
}));

const ShadowOverlay = styled(Box)(({ theme }) => ({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    zIndex: 9,
}));

interface CP82BoxProps {
    orderId?: OrderId;
    activeDoors: number[];
    title: string;
}

const CP82Box = (props: CP82BoxProps) => {
    // const InfoIcon = Assets.icon.infoIcon.component;
    const [infoOpen, setInfoOpen] = useState<boolean>(false);
    const activeDoor = props.activeDoors[0]; // Select first item in available doors
    const enableInfo = Boolean(props.orderId);
    
    const getLeftSize = (columnOrder: number) => {
        // Each column is 60px wide, so left position is (columnOrder - 1) * 60
        return (columnOrder - 1) * 60;
    };
    
    const renderColumnWithCU = (
        activeDoor: number, 
        columnOrder: number, 
        size: number, 
        doorsHeight: number, 
        firstDoorNumber: number,
        cuHeight: number,
        cuNumber: number,
    ) => {
        const leftSize = getLeftSize(columnOrder);
        const doors = [];
        let topOffset = 32;
        let doorCounter = firstDoorNumber;

        for (let i = 0; i < size; i++) {
            if (i === cuNumber - 1){
                // Control Unit - no number, gray background
                doors.push({ top: topOffset, height: cuHeight, doorNumber: 0, isCU: true });
                topOffset += cuHeight;
            } else {
                doors.push({ top: topOffset, height: doorsHeight, doorNumber: doorCounter, isCU: false });
                topOffset += doorsHeight;
                doorCounter++;
            }
        }

        return doors.map((door, index) => (
            <CP82BoxDoor
                key={`${door.doorNumber}-${index}`}
                top={door.top}
                left={leftSize ?? 0}
                height={door.height}
                doorNumber={door.doorNumber}
                isCU={door.isCU}
                active={
                    !door.isCU && 
                    ((activeDoor - firstDoorNumber + 1 < cuNumber) && (activeDoor === door.doorNumber)) 
                        ? 
                        true 
                        :
                        ((activeDoor - firstDoorNumber + 1 >= cuNumber - 1) 
                        && (activeDoor === door.doorNumber)) 
                            ? true 
                            : false
                }
            />
        ));
    };
    
    const renderColumn = (
        activeDoor: number, 
        columnOrder: number, 
        size: number, 
        doorsHeight: number, 
        firstDoorNumber: number,
    ) => {
        const leftSize = getLeftSize(columnOrder);
        return Array.from({ length: size }, (_, index) => {
            const top = 32 + index * (doorsHeight);
            const doorNumber = firstDoorNumber + index;
            return (
                <CP82BoxDoor 
                    key={doorNumber} 
                    top={top} 
                    left={leftSize ?? 0} 
                    height={doorsHeight} 
                    doorNumber={doorNumber} 
                    active={index + 1 === activeDoor - firstDoorNumber + 1 ? true : false} 
                />
            );
        });
    };

    const renderFirstColumn = (activeDoor: number, columnOrder: number) => {
        const size = 6; // Number of doors in the column       
        const doorsHeight = 45; 
        const firstDoorNumber = 77;
        return renderColumn(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber);
    };
    
    const renderSecondColumn = (activeDoor: number, columnOrder: number) => {
        const size = 5;       
        const doorsHeight = 54; 
        const firstDoorNumber = 72;
        return renderColumn(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber);
    };
    const renderThirdColumn = (activeDoor: number, columnOrder: number) => {
        const size = 6;        
        const doorsHeight = 45; 
        const firstDoorNumber = 66;
        return renderColumn(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber);
    };
    const renderFourthColumn = (activeDoor: number, columnOrder: number) => {
        const size = 5;        
        const doorsHeight = 54; 
        const firstDoorNumber = 61;
        return renderColumn(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber);
    };
    const renderFifthColumn = (activeDoor: number, columnOrder: number) => {
        const size = 6;        
        const doorsHeight = 45; 
        const firstDoorNumber = 55;
        return renderColumn(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber);
    };
    const renderSixthColumn = (activeDoor: number, columnOrder: number) => {
        const size = 5;        
        const doorsHeight = 54; 
        const firstDoorNumber = 50;
        return renderColumn(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber);
    };
    const renderSeventhColumn = (activeDoor: number, columnOrder: number) => {
        const size = 6;        
        const doorsHeight = 45; 
        const firstDoorNumber = 44;
        return renderColumn(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber);
    };
    const renderEighthColumn = (activeDoor: number, columnOrder: number) => {
        const size = 5;        
        const doorsHeight = 54; 
        const firstDoorNumber = 39;
        return renderColumn(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber);
    };
    const renderNinthColumn = (activeDoor: number, columnOrder: number) => {
        const size = 6;        
        const doorsHeight = 45; 
        const firstDoorNumber = 33;
        return renderColumn(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber);
    };
    const renderTenthColumn = (activeDoor: number, columnOrder: number) => {
        const size = 5;        
        const doorsHeight = 54; 
        const firstDoorNumber = 28;
        return renderColumn(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber);
    };

    const renderEleventhColumn = (activeDoor: number, columnOrder: number) => {
        const size = 12;        
        const doorsHeight = 22.5; 
        const firstDoorNumber = 16;
        return renderColumn(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber);
    };

    const renderTwelfthColumn = (activeDoor: number, columnOrder: number) => {
        const size = 8;        
        const doorsHeight = 33.75; 
        const firstDoorNumber = 8;
        return renderColumn(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber);
    };

    const renderThirteenthColumn = (activeDoor: number, columnOrder: number) => {
        const size = 8;        
        const doorsHeight = 33.75; 
        const firstDoorNumber = 1;
        const cuHeight = 67.5;
        const cuNumber = 2;
        return renderColumnWithCU(activeDoor, columnOrder, size, doorsHeight, firstDoorNumber, cuHeight, cuNumber);
    };

    // Determine which column contains the active door
    const getActiveColumn = (doorNumber: number): number => {
        if (doorNumber >= 77 && doorNumber <= 82) return 1;
        if (doorNumber >= 72 && doorNumber <= 76) return 2;
        if (doorNumber >= 66 && doorNumber <= 71) return 3;
        if (doorNumber >= 61 && doorNumber <= 65) return 4;
        if (doorNumber >= 55 && doorNumber <= 60) return 5;
        if (doorNumber >= 50 && doorNumber <= 54) return 6;
        if (doorNumber >= 44 && doorNumber <= 49) return 7;
        if (doorNumber >= 39 && doorNumber <= 43) return 8;
        if (doorNumber >= 33 && doorNumber <= 38) return 9;
        if (doorNumber >= 28 && doorNumber <= 32) return 10;
        if (doorNumber >= 16 && doorNumber <= 27) return 11;
        if (doorNumber >= 8 && doorNumber <= 15) return 12;
        if (doorNumber >= 1 && doorNumber <= 7) return 13;
        return 1; // Default to first column
    };

    // Calculate which columns to show (active, 1 before, 2 after)
    const getVisibleColumns = (activeColumn: number): number[] => {
        const totalColumns = 13;
        
        if (activeColumn <= 2) {
            // Show first 4 columns
            return [1, 2, 3, 4];
        } else if (activeColumn >= totalColumns - 1) {
            // Show last 4 columns
            return [totalColumns - 3, totalColumns - 2, totalColumns - 1, totalColumns];
        } else {
            // Show active, 1 before, 2 after
            return [activeColumn - 1, activeColumn, activeColumn + 1, activeColumn + 2];
        }
    };

    const activeColumn = getActiveColumn(activeDoor);
    const visibleColumns = getVisibleColumns(activeColumn);
    const firstVisibleColumn = visibleColumns[0];

    // Adjust getLeftSize to account for the offset
    const getAdjustedLeftSize = (columnOrder: number) => {
        return (columnOrder - firstVisibleColumn) * 60;
    };

    const renderColumnByNumber = (columnNumber: number) => {
        const activeDoorNum = props.activeDoors[0];
        // Adjust the column order to be relative to the first visible column
        const adjustedColumnOrder = columnNumber - firstVisibleColumn + 1;
        
        switch(columnNumber) {
            case 1: return renderFirstColumn(activeDoorNum, adjustedColumnOrder);
            case 2: return renderSecondColumn(activeDoorNum, adjustedColumnOrder);
            case 3: return renderThirdColumn(activeDoorNum, adjustedColumnOrder);
            case 4: return renderFourthColumn(activeDoorNum, adjustedColumnOrder);
            case 5: return renderFifthColumn(activeDoorNum, adjustedColumnOrder);
            case 6: return renderSixthColumn(activeDoorNum, adjustedColumnOrder);
            case 7: return renderSeventhColumn(activeDoorNum, adjustedColumnOrder);
            case 8: return renderEighthColumn(activeDoorNum, adjustedColumnOrder);
            case 9: return renderNinthColumn(activeDoorNum, adjustedColumnOrder);
            case 10: return renderTenthColumn(activeDoorNum, adjustedColumnOrder);
            case 11: return renderEleventhColumn(activeDoorNum, adjustedColumnOrder);
            case 12: return renderTwelfthColumn(activeDoorNum, adjustedColumnOrder);
            case 13: return renderThirteenthColumn(activeDoorNum, adjustedColumnOrder);
            default: return null;
        }
    };

    return (
        <Root>
            <Rectangle style={{ backgroundColor: "black" }}>
                <Title style={{ color: "white" }}>{props.title}</Title>
                {enableInfo && (
                    <Info onClick={() => setInfoOpen(true)}>
                        {/* <InfoIcon /> */}
                    </Info>
                )}
            </Rectangle>
            <Box sx={{ position: 'absolute', width: '240px', height: '100%', left: 0, top: 0 }}>
                {visibleColumns.map(columnNumber => renderColumnByNumber(columnNumber))}
            </Box>
            <Grow in={infoOpen} style={{ transformOrigin: '0 0 0' }}>
                <ShadowOverlay onClick={() => setInfoOpen(false)}>
                    <KoluluBoxInfoBox onClose={() => setInfoOpen(false)} orderId={props.orderId!} />
                </ShadowOverlay>
            </Grow>
        </Root>
    );
};

export default CP82Box;
